import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Grid,
  Chip,
  Pagination,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Toolbar,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { getPurchaseOrders } from '../../collections/purchaseOrders';
import { getSuppliers } from '../../collections/suppliers';

const PurchaseOrderList = ({ controlRole }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [historicalFilter, setHistoricalFilter] = useState(null); // null = all, 'true' = historical only, 'false' = regular only
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const brandColors = {
    primary: '#0e2a04',
    secondary: '#6b8e4e',
    accent: '#d4a574',
    light: '#c8d6b8',
    background: '#f5f8f3',
  };

  const statusOptions = [
    { value: 'All', label: 'Tất cả trạng thái' },
    { value: 'DRAFT', label: 'Nháp' },
    { value: 'SENT', label: 'Đã gửi' },
    { value: 'PARTIALLY_RECEIVED', label: 'Nhận một phần' },
    { value: 'FULLY_RECEIVED', label: 'Nhận đầy đủ' },
    { value: 'CLOSED', label: 'Đã đóng' },
    { value: 'CANCELLED', label: 'Đã hủy' },
  ];

  const historicalOptions = [
    { value: null, label: 'Tất cả' },
    { value: 'false', label: 'Đơn thường' },
    { value: 'true', label: 'Đơn lịch sử' },
  ];

  const statusColors = {
    DRAFT: { bg: '#f4f1e8', color: '#8b7355' },
    SENT: { bg: '#e8f2e3', color: '#6b8e4e' },
    PARTIALLY_RECEIVED: { bg: '#f9f0e3', color: '#d4a574' },
    FULLY_RECEIVED: { bg: '#d8e8d2', color: '#1a4f0a' },
    CLOSED: { bg: '#c8d6b8', color: '#0e2a04' },
    CANCELLED: { bg: '#f0ede8', color: '#8b7355' },
  };

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        page,
        per_page: 20,
        search: searchTerm,
        status: selectedStatus?.value === 'All' ? undefined : selectedStatus?.value,
        supplier_id: controlRole === 'STAFF' ? undefined : selectedSupplier?.id,
        is_historical: historicalFilter?.value,
        date_from: dateFrom,
        date_to: dateTo,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      // Staff users don't need supplier data
      if (controlRole === 'STAFF') {
        const poData = await getPurchaseOrders(params);
        setPurchaseOrders(poData.data || []);
        setTotalPages(poData.total_pages || 1);
      } else {
        // Admin users can load suppliers
        const [poData, suppliersData] = await Promise.all([
          getPurchaseOrders(params),
          suppliers.length === 0 ? getSuppliers({ per_page: 1000 }) : Promise.resolve({ data: suppliers }),
        ]);

        setPurchaseOrders(poData.data || []);
        setTotalPages(poData.total_pages || 1);
        
        if (suppliers.length === 0) {
          setSuppliers(suppliersData.data || []);
        }
      }
    } catch (err) {
      console.error('Error loading purchase orders:', err);
      setError('Không thể tải danh sách đơn đặt hàng. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, selectedSupplier, selectedStatus, historicalFilter, dateFrom, dateTo, sortBy, sortOrder, controlRole, suppliers.length]);

  useEffect(() => {
    // Only load data when controlRole is available
    if (controlRole) {
      loadData();
    }
  }, [loadData, controlRole]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  }, []);

  const handleSupplierChange = useCallback((event, newValue) => {
    setSelectedSupplier(newValue);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((event, newValue) => {
    setSelectedStatus(newValue);
    setPage(1);
  }, []);

  const handleHistoricalFilterChange = useCallback((event, newValue) => {
    setHistoricalFilter(newValue);
    setPage(1);
  }, []);

  // Show loading state while waiting for controlRole
  if (!controlRole) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSupplier(null);
    setSelectedStatus(null);
    setHistoricalFilter(null);
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const getStatusLabel = (status) => {
    // Handle both "DRAFT" and "Draft" formats
    const normalizedStatus = status?.toString().toUpperCase();
    const option = statusOptions.find(opt => opt.value === normalizedStatus);
    return option ? option.label : status;
  };

  const StatusChip = ({ status }) => {
    const normalizedStatus = status?.toString().toUpperCase();
    const colors = statusColors[normalizedStatus] || { bg: '#f0f0f0', color: '#666' };
    return (
      <Chip
        label={getStatusLabel(status)}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.color,
          fontWeight: 500,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      />
    );
  };

  // Mobile card component
  const PurchaseOrderCard = ({ po }) => (
    <Card sx={{
      mb: 2,
      borderRadius: '12px',
      boxShadow: `0 4px 12px rgba(14, 42, 4, 0.1)`,
      border: po.is_historical ? `2px solid #9e9e9e` : `1px solid rgba(107, 142, 78, 0.1)`
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {po.is_historical && (
              <Chip
                label="Lịch sử"
                size="small"
                sx={{
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: '20px',
                  fontWeight: 500,
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              />
            )}
            <Typography variant="h6" sx={{
              color: brandColors.primary,
              fontWeight: 600,
              fontFamily: 'Poppins, Inter, system-ui, sans-serif'
            }}>
              {po.po_number}
            </Typography>
          </Box>
          <StatusChip status={po.status} />
        </Box>
        
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          {controlRole !== 'STAFF' && (
            <>
              <strong>Nhà cung cấp:</strong> {po.supplier?.name}
            </>
          )}
        </Typography>
        
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          <strong>Ngày đặt:</strong> {new Date(po.order_date).toLocaleDateString('vi-VN')}
        </Typography>
        
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {controlRole !== 'STAFF' && (
            <>
              <strong>Tổng tiền:</strong> {po.total_received_amount?.toLocaleString('vi-VN')} đ
            </>
          )}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/purchase-orders/${po.id}`)}
            sx={{ 
              color: brandColors.primary,
              '&:hover': { backgroundColor: `${brandColors.light}30` }
            }}
          >
            <ViewIcon />
          </IconButton>
          {po.status === 'DRAFT' && controlRole !== 'STAFF' && (
            <IconButton
              size="small"
              onClick={() => navigate(`/purchase-orders/${po.id}/edit`)}
              sx={{ 
                color: brandColors.secondary,
                '&:hover': { backgroundColor: `${brandColors.light}30` }
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && purchaseOrders.length === 0) {
    return (
      <Container>
        <Typography>Đang tải...</Typography>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xl">
      <Typography 
        component="h1" 
        variant="h4" 
        sx={{ 
          mb: 4,
          color: brandColors.primary,
          fontWeight: 700,
          fontFamily: 'Poppins, Inter, system-ui, sans-serif',
          textAlign: 'center',
          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
      >
        Danh sách đơn đặt hàng
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters Section */}
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: '16px',
        background: `linear-gradient(135deg, ${brandColors.background} 0%, #ffffff 100%)`,
        boxShadow: `0 8px 32px rgba(14, 42, 4, 0.08)`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <FilterIcon sx={{ color: brandColors.primary, mr: 1 }} />
          <Typography variant="h6" sx={{ 
            color: brandColors.primary,
            fontWeight: 600,
            fontFamily: 'Poppins, Inter, system-ui, sans-serif' 
          }}>
            Bộ lọc
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder={controlRole === 'STAFF' ? "Tìm theo số PO..." : "Tìm theo số PO hoặc nhà cung cấp..."}
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: brandColors.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  '&:hover': {
                    borderColor: brandColors.secondary,
                  },
                  '&.Mui-focused': {
                    borderColor: brandColors.primary,
                  }
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Autocomplete
              value={selectedStatus}
              onChange={handleStatusChange}
              options={statusOptions}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Trạng thái"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: '#ffffff',
                    }
                  }}
                />
              )}
            />
          </Grid>

          {controlRole !== 'STAFF' && (
            <Grid item xs={12} md={2}>
              <Autocomplete
                value={historicalFilter}
                onChange={handleHistoricalFilterChange}
                options={historicalOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Loại đơn"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                  />
                )}
              />
            </Grid>
          )}

          {controlRole !== 'STAFF' && (
            <Grid item xs={12} md={3}>
              <Autocomplete
                value={selectedSupplier}
                onChange={handleSupplierChange}
                options={suppliers}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Nhà cung cấp"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Từ ngày"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={1}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              sx={{
                borderRadius: '12px',
                borderColor: brandColors.secondary,
                color: brandColors.primary,
                height: '56px',
                '&:hover': {
                  borderColor: brandColors.primary,
                  backgroundColor: brandColors.light,
                }
              }}
            >
              Xóa
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body1" color="textSecondary">
          Tìm thấy {purchaseOrders.length > 0 ? purchaseOrders.length : 0} đơn đặt hàng
        </Typography>
        
        {controlRole !== 'STAFF' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/purchase-orders/new')}
            sx={{
              background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
              borderRadius: '12px',
              px: 3,
            py: 1,
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 600,
            boxShadow: `0 8px 24px rgba(14, 42, 4, 0.3)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${brandColors.secondary} 0%, ${brandColors.primary} 100%)`,
              transform: 'translateY(-1px)',
            }
          }}
        >
          Tạo đơn mới
          </Button>
        )}
      </Box>

      {/* Purchase Orders List */}
      {isMobile ? (
        // Mobile card view
        <Box>
          {purchaseOrders.map((po) => (
            <PurchaseOrderCard key={po.id} po={po} />
          ))}
        </Box>
      ) : (
        // Desktop table view
        <Paper sx={{ 
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: `0 8px 32px rgba(14, 42, 4, 0.08)`,
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: `linear-gradient(135deg, ${brandColors.background} 0%, #ffffff 100%)`,
                }}>
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary, fontFamily: 'Poppins, Inter, system-ui, sans-serif' }}>
                    <TableSortLabel
                      active={sortBy === 'po_number'}
                      direction={sortBy === 'po_number' ? sortOrder : 'asc'}
                      onClick={() => handleSort('po_number')}
                      sx={{ color: brandColors.primary }}
                    >
                      Số PO
                    </TableSortLabel>
                  </TableCell>
                  {controlRole !== 'STAFF' && (
                    <TableCell sx={{ fontWeight: 600, color: brandColors.primary, fontFamily: 'Poppins, Inter, system-ui, sans-serif' }}>
                      <TableSortLabel
                        active={sortBy === 'supplier'}
                        direction={sortBy === 'supplier' ? sortOrder : 'asc'}
                        onClick={() => handleSort('supplier')}
                        sx={{ color: brandColors.primary }}
                      >
                        Nhà cung cấp
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary, fontFamily: 'Poppins, Inter, system-ui, sans-serif' }}>
                    <TableSortLabel
                      active={sortBy === 'order_date'}
                      direction={sortBy === 'order_date' ? sortOrder : 'asc'}
                      onClick={() => handleSort('order_date')}
                      sx={{ color: brandColors.primary }}
                    >
                      Ngày đặt
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary, fontFamily: 'Poppins, Inter, system-ui, sans-serif' }}>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortOrder : 'asc'}
                      onClick={() => handleSort('status')}
                      sx={{ color: brandColors.primary }}
                    >
                      Trạng thái
                    </TableSortLabel>
                  </TableCell>
                  {controlRole !== 'STAFF' && (
                    <TableCell sx={{ fontWeight: 600, color: brandColors.primary, fontFamily: 'Poppins, Inter, system-ui, sans-serif' }}>
                      <TableSortLabel
                        active={sortBy === 'total_received_amount'}
                        direction={sortBy === 'total_received_amount' ? sortOrder : 'asc'}
                        onClick={() => handleSort('total_received_amount')}
                        sx={{ color: brandColors.primary }}
                      >
                        Tổng tiền
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, color: brandColors.primary, fontFamily: 'Poppins, Inter, system-ui, sans-serif', width: 120 }}>
                    Thao tác
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrders.map((po, index) => (
                  <TableRow key={po.id} sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'rgba(245, 248, 243, 0.3)'
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(107, 142, 78, 0.05)',
                    }
                  }}>
                    <TableCell sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {po.is_historical && (
                          <Chip
                            label="Lịch sử"
                            size="small"
                            sx={{
                              backgroundColor: '#9e9e9e',
                              color: 'white',
                              fontSize: '0.7rem',
                              height: '20px',
                              fontWeight: 500,
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          />
                        )}
                        {po.po_number}
                      </Box>
                    </TableCell>
                    {controlRole !== 'STAFF' && (
                      <TableCell sx={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                        {po.supplier?.name}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {new Date(po.order_date).toLocaleDateString('vi-VN')}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={po.status} />
                    </TableCell>
                    {controlRole !== 'STAFF' && (
                      <TableCell sx={{ 
                        fontFamily: 'Inter, system-ui, sans-serif', 
                        fontWeight: 600,
                        color: brandColors.primary 
                      }}>
                        {po.total_received_amount?.toLocaleString('vi-VN')} đ
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/purchase-orders/${po.id}`)}
                          sx={{ 
                            color: brandColors.primary,
                            '&:hover': { backgroundColor: `${brandColors.light}30` }
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                        {po.status === 'DRAFT' && controlRole !== 'STAFF' && (
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/purchase-orders/${po.id}/edit`)}
                            sx={{ 
                              color: brandColors.secondary,
                              '&:hover': { backgroundColor: `${brandColors.light}30` }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Empty State */}
      {!loading && purchaseOrders.length === 0 && (
        <Paper sx={{ 
          p: 6, 
          textAlign: 'center', 
          borderRadius: '16px',
          boxShadow: `0 8px 32px rgba(14, 42, 4, 0.08)`,
        }}>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
            Không tìm thấy đơn đặt hàng nào
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/purchase-orders/new')}
            sx={{
              background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
              borderRadius: '12px',
            }}
          >
            Tạo đơn đặt hàng đầu tiên
          </Button>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                color: brandColors.primary,
                fontFamily: 'Inter, system-ui, sans-serif',
                '&.Mui-selected': {
                  backgroundColor: brandColors.primary,
                }
              }
            }}
          />
        </Box>
      )}
    </Container>
  );
};

export default PurchaseOrderList;